import React from "react";
import { motion } from "framer-motion";
import { 
  UserPlus, 
  Video, 
  Share2, 
  ShoppingBag,
  Users,
  Zap
} from "lucide-react";

export default function HowItWorks() {
  const steps = [
    {
      step: 1,
      icon: UserPlus,
      title: "Sign Up & Explore",
      description: "Create your account and discover the world of reels, pixs, collections, and products.",
      color: "bg-grad-orange-1"
    },
    {
      step: 2,
      icon: "/images/reels_icon.png",
      title: "Create Short Reels",
      description: "Use our powerful tools to create engaging short-form video content that captivates your audience.",
      color: "bg-brand-orange"
    },
    {
      step: 3,
      icon: "/images/pouch_icon.png",
      title: "Share Pixs & Pouches",
      description: "Share your favorite moments and create pix pouches of inspiring public pixs.",
      color: "bg-grad-orange-2"
    },
    {
      step: 4,
      icon: "/images/shop_icon.png",
      title: "Shop & Discover",
      description: "Browse and purchase amazing products from creators and brands within the platform.",
      color: "bg-orange-old"
    },
    {
      step: 5,
      icon: Users,
      title: "Community",
      description: "Our platform brings together creators, collectors, and shoppers in one unified experience.",
      color: "bg-grad-orange-1"
    },
    {
      step: 6,
      icon: Zap,
      title: "Performance",
      description: "Lightning-fast platform that seamlessly handles reels, pixs, collections, and shopping.",
      color: "bg-brand-orange"
    }
  ];

  return (
    <section className="py-12 md:py-16 bg-gradient-to-br from-background to-secondary/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-10 md:mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            How It{" "}
            <span className="bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
              Works
            </span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed px-4">
            Experience VREELS through our comprehensive platform. Create, share, discover, shop, and connect with our vibrant community.
          </p>
        </motion.div>

        {/* Steps in Grid Layout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-6 xl:gap-8"
        >
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                viewport={{ once: true }}
                className="group relative"
              >
                <div className="h-full p-4 sm:p-5 lg:p-6 rounded-xl bg-card border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:scale-105 relative overflow-hidden">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 overflow-hidden">
                    <div className={`absolute -right-6 sm:-right-8 -top-6 sm:-top-8 w-24 sm:w-28 lg:w-32 h-24 sm:h-28 lg:h-32 bg-gradient-to-br ${step.color} opacity-5 rounded-full blur-2xl`} />
                    {step.step === 1 && (
                      <>
                        {/* User profile circles */}
                        <div className="absolute top-4 right-4 w-8 h-8 bg-gradient-to-br from-blue-500/40 to-cyan-500/40 rounded-full border border-white/30"></div>
                        <div className="absolute top-8 right-8 w-6 h-6 bg-gradient-to-br from-blue-400/35 to-cyan-400/35 rounded-full border border-white/25"></div>
                        <div className="absolute bottom-6 right-6 w-7 h-7 bg-gradient-to-br from-blue-300/40 to-cyan-300/40 rounded-full border border-white/30"></div>
                        {/* Plus icon pattern */}
                        <div className="absolute bottom-4 right-12 w-6 h-1 bg-blue-400/30 rounded-full"></div>
                        <div className="absolute bottom-1 right-14 w-1 h-6 bg-blue-400/30 rounded-full"></div>
                      </>
                    )}
                    {step.step === 2 && (
                      <>
                        {/* Video camera pattern */}
                        <div className="absolute top-6 right-6 w-12 h-10 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-lg rotate-12 flex items-center justify-center">
                          <div className="w-4 h-4 bg-white/40 rounded-full"></div>
                        </div>
                        <div className="absolute bottom-8 right-8 w-6 h-6 bg-gradient-to-br from-purple-400/40 to-pink-400/40 rounded-full"></div>
                        <div className="absolute top-12 right-12 w-3 h-3 bg-gradient-to-br from-purple-300/50 to-pink-300/50 rounded-full"></div>
                        {/* Film strip pattern */}
                        <div className="absolute bottom-0 right-0 w-full h-6 bg-gradient-to-r from-transparent via-purple-500/10 to-pink-500/10">
                          <div className="flex h-full items-center justify-end space-x-1 pr-3">
                            <div className="w-1.5 h-1.5 bg-purple-400/30 rounded-full"></div>
                            <div className="w-1.5 h-1.5 bg-pink-400/30 rounded-full"></div>
                            <div className="w-1.5 h-1.5 bg-purple-400/30 rounded-full"></div>
                          </div>
                        </div>
                      </>
                    )}
                    {step.step === 3 && (
                      <>
                        {/* Photo frames */}
                        <div className="absolute top-4 right-4 w-10 h-7 bg-gradient-to-br from-green-500/30 to-emerald-500/30 rounded-md rotate-12 border border-white/20"></div>
                        <div className="absolute top-6 right-8 w-8 h-6 bg-gradient-to-br from-green-400/25 to-emerald-400/25 rounded-md -rotate-6 border border-white/15"></div>
                        <div className="absolute bottom-6 right-6 w-6 h-5 bg-gradient-to-br from-green-300/30 to-emerald-300/30 rounded-md rotate-45 border border-white/20"></div>
                        {/* Share arrows */}
                        <div className="absolute bottom-4 right-12 w-5 h-0.5 bg-gradient-to-r from-green-400/40 to-emerald-400/40 rounded-full"></div>
                        <div className="absolute bottom-3 right-10 w-1.5 h-1.5 bg-green-400/40 rounded-full"></div>
                        {/* Collection dots */}
                        <div className="absolute bottom-8 right-8 grid grid-cols-2 gap-1">
                          <div className="w-1.5 h-1.5 bg-green-400/30 rounded-sm"></div>
                          <div className="w-1.5 h-1.5 bg-emerald-400/30 rounded-sm"></div>
                          <div className="w-1.5 h-1.5 bg-emerald-400/30 rounded-sm"></div>
                          <div className="w-1.5 h-1.5 bg-green-400/30 rounded-sm"></div>
                        </div>
                      </>
                    )}
                    {step.step === 4 && (
                      <>
                        {/* Shopping bag */}
                        <div className="absolute top-4 right-4 w-10 h-8 bg-gradient-to-br from-orange-500/30 to-red-500/30 rounded-lg border border-white/20">
                          <div className="w-5 h-1.5 bg-white/20 rounded-full mx-auto mt-1.5"></div>
                        </div>
                        {/* Price tags */}
                        <div className="absolute top-6 right-10 w-5 h-3 bg-gradient-to-br from-orange-400/40 to-red-400/40 rounded-md rotate-12 flex items-center justify-center">
                          <div className="w-0.5 h-0.5 bg-white/60 rounded-full"></div>
                        </div>
                        {/* Shopping cart items */}
                        <div className="absolute bottom-4 right-4 space-y-1">
                          <div className="w-6 h-0.5 bg-orange-400/30 rounded-full"></div>
                          <div className="w-5 h-0.5 bg-red-400/30 rounded-full"></div>
                          <div className="w-5.5 h-0.5 bg-orange-300/30 rounded-full"></div>
                        </div>
                        {/* Product discovery dots */}
                        <div className="absolute bottom-8 right-8 flex space-x-1">
                          <div className="w-1.5 h-1.5 bg-orange-400/40 rounded-full"></div>
                          <div className="w-1.5 h-1.5 bg-red-400/40 rounded-full"></div>
                          <div className="w-1.5 h-1.5 bg-orange-300/40 rounded-full"></div>
                        </div>
                      </>
                    )}
                    {step.step === 5 && (
                      <>
                        {/* Community user avatars */}
                        <div className="absolute top-4 right-4 w-8 h-8 bg-gradient-to-br from-blue-500/40 to-indigo-500/40 rounded-full border border-white/30"></div>
                        <div className="absolute top-6 right-10 w-6 h-6 bg-gradient-to-br from-blue-400/35 to-indigo-400/35 rounded-full border border-white/25"></div>
                        <div className="absolute top-10 right-6 w-7 h-7 bg-gradient-to-br from-blue-300/40 to-indigo-300/40 rounded-full border border-white/30"></div>
                        {/* Connection lines */}
                        <div className="absolute top-8 right-8 w-4 h-0.5 bg-gradient-to-r from-blue-400/30 to-indigo-400/30 rounded-full rotate-45"></div>
                        <div className="absolute top-9 right-7 w-3 h-0.5 bg-gradient-to-r from-blue-300/30 to-indigo-300/30 rounded-full -rotate-45"></div>
                        {/* Community icons */}
                        <div className="absolute bottom-6 right-6 flex space-x-1">
                          <div className="w-2 h-2 bg-blue-400/40 rounded-full"></div>
                          <div className="w-2 h-2 bg-indigo-400/40 rounded-full"></div>
                          <div className="w-2 h-2 bg-blue-300/40 rounded-full"></div>
                        </div>
                      </>
                    )}
                    {step.step === 6 && (
                      <>
                        {/* Performance lightning patterns */}
                        <div className="absolute top-4 right-4 w-3 h-8 bg-gradient-to-b from-purple-500/40 to-violet-500/40 transform rotate-12 clip-path-lightning"></div>
                        <div className="absolute top-6 right-8 w-2 h-6 bg-gradient-to-b from-purple-400/35 to-violet-400/35 transform -rotate-12"></div>
                        <div className="absolute bottom-6 right-6 w-4 h-6 bg-gradient-to-b from-purple-300/40 to-violet-300/40 transform rotate-45"></div>
                        {/* Speed indicators */}
                        <div className="absolute bottom-4 right-10 w-6 h-0.5 bg-gradient-to-r from-purple-400/40 to-violet-400/40 rounded-full"></div>
                        <div className="absolute bottom-6 right-12 w-4 h-0.5 bg-gradient-to-r from-purple-300/30 to-violet-300/30 rounded-full"></div>
                        <div className="absolute bottom-8 right-14 w-2 h-0.5 bg-gradient-to-r from-purple-200/30 to-violet-200/30 rounded-full"></div>
                        {/* Performance dots */}
                        <div className="absolute bottom-8 right-8 grid grid-cols-3 gap-0.5">
                          <div className="w-1 h-1 bg-purple-400/40 rounded-full"></div>
                          <div className="w-1 h-1 bg-violet-400/40 rounded-full"></div>
                          <div className="w-1 h-1 bg-purple-300/40 rounded-full"></div>
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Step Number Badge */}
                  <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                    <div className={`w-6 sm:w-7 lg:w-8 h-6 sm:h-7 lg:h-8 bg-gradient-to-br ${step.color} rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      {step.step}
                    </div>
                  </div>

                  {/* Icon */}
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl shadow-lg mb-4 ${step.color}`}>
                    {typeof step.icon === "string" ? (
                      <img src={step.icon} alt={step.title} className="h-6 w-6" />
                    ) : (
                      <step.icon className="h-6 w-6 text-white" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 group-hover:text-primary transition-colors duration-300 leading-tight">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground text-sm sm:text-sm lg:text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}